import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createTechnology,
  fetchAllTechnologies,
  fetchTechnologiesByAccountId,
  fetchTechnologyById,
  fetchTechnologyRelatedObjects,
  fetchTechnologyCategories,
  updateTechnology,
  UpdateTechnologyRequest,
  deleteTechnology,
} from "@/services/frontendServices/technologiesServices";
import { TechnologyDto } from "@/types";
import { TechnologyFormData } from "@/components/forms/create-technology-form";
import { RelatedObjectType } from "@/enums";
import { accountKeys } from "./useAccounts";
import { getErrorMessage, showErrorToast } from "@/lib/error-handling";

// Keys for queries, to maintain consistency
export const technologyKeys = {
  all: ["technologies"] as const,
  lists: () => [...technologyKeys.all, "list"] as const,
  list: (accountId: string) => [...technologyKeys.lists(), accountId] as const,
  details: () => [...technologyKeys.all, "detail"] as const,
  detail: (id: string) => [...technologyKeys.details(), id] as const,
};

export const useAllTechnologies = () => {
  return useQuery({
    queryKey: [...technologyKeys.all, "global"],
    queryFn: fetchAllTechnologies,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
};

export const useTechnologiesByAccount = (accountId: string) => {
  return useQuery({
    queryKey: technologyKeys.list(accountId),
    queryFn: () => fetchTechnologiesByAccountId(accountId),
    enabled: !!accountId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

export const useCreateTechnology = () => {
  const queryClient = useQueryClient();

  return useMutation<TechnologyDto, Error, TechnologyFormData>({
    mutationFn: (formData: TechnologyFormData) => createTechnology(formData),
    onSuccess: (newTechnology, variables) => {
      const accountId = variables.accountId;
      if (accountId) {
        // Optimistically update the cache
        queryClient.setQueryData<TechnologyDto[]>(
          technologyKeys.list(accountId),
          (oldData = []) => {
            return [newTechnology, ...oldData];
          }
        );

        // Invalidates the account's technologies list query to force a refetch
        queryClient.invalidateQueries({
          queryKey: technologyKeys.list(accountId),
        });

        // Invalidate org-wide list
        queryClient.invalidateQueries({
          queryKey: [...technologyKeys.all, "global"],
        });

        // Invalidate account cache to update last updated date
        queryClient.invalidateQueries({
          queryKey: accountKeys.detail(accountId),
        });

        toast.success("Technology saved successfully!", {
          description: `Technology has been created.`,
          duration: 3000,
        });
      }
    },
    onError: (error) => {
      showErrorToast(error, {
        title: "Error creating technology",
        defaultMessage: "Failed to create technology",
        duration: 5000,
      });
    },
  });
};

export const useTechnology = (technologyId: string) => {
  return useQuery({
    queryKey: technologyKeys.detail(technologyId),
    queryFn: () => fetchTechnologyById(technologyId),
    enabled: !!technologyId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

interface UseTechnologyRelatedObjectsParams {
  technologyId: string;
  types: RelatedObjectType[];
}

export function useTechnologyRelatedObjects({
  technologyId,
  types,
}: UseTechnologyRelatedObjectsParams) {
  return useQuery({
    queryKey: ["technologyRelatedObjects", technologyId, types],
    queryFn: async () => {
      return fetchTechnologyRelatedObjects(technologyId, types);
    },
    enabled: !!technologyId && !!types.length,
  });
}

export const useUpdateTechnology = ({
  showSuccessToast = true,
}: { showSuccessToast?: boolean } = {}) => {
  const queryClient = useQueryClient();

  return useMutation<
    TechnologyDto,
    Error,
    { technologyId: string; formData: UpdateTechnologyRequest }
  >({
    mutationFn: ({ technologyId, formData }) =>
      updateTechnology(technologyId, formData),
    onSuccess: (updatedTechnology, variables) => {
      const { technologyId, formData } = variables;

      // Update the individual technology detail
      queryClient.setQueryData<TechnologyDto>(
        technologyKeys.detail(technologyId),
        updatedTechnology
      );

      // Update the specific account's technology list
      const accountId = updatedTechnology.account?.id;
      if (accountId) {
        queryClient.setQueryData<TechnologyDto[]>(
          technologyKeys.list(accountId),
          (oldData) => {
            if (!oldData) return oldData;
            return oldData.map((technology) =>
              technology.id === updatedTechnology.id
                ? updatedTechnology
                : technology
            );
          }
        );
      }

      // Invalidate related queries to force a refetch
      queryClient.invalidateQueries({
        queryKey: technologyKeys.detail(technologyId),
      });

      // Invalidate org-wide list
      queryClient.invalidateQueries({
        queryKey: [...technologyKeys.all, "global"],
      });

      // Invalidate related objects queries to force a refetch
      queryClient.invalidateQueries({
        queryKey: ["technologyRelatedObjects", technologyId],
      });

      // Invalidate account cache to update last updated date
      if (accountId) {
        queryClient.invalidateQueries({
          queryKey: accountKeys.detail(accountId),
        });
      }

      if (!showSuccessToast) return;

      // Show specific messages for archive/unarchive operations
      if (formData.archived !== undefined) {
        const action = formData.archived ? "archived" : "unarchived";
        toast.success(`Technology ${action} successfully!`, {
          description: `Technology has been ${action}.`,
          duration: 3000,
        });
      } else {
        toast.success("Technology updated successfully!", {
          description: `Technology has been updated.`,
          duration: 3000,
        });
      }
    },
    onError: (error, variables) => {
      const errorMessage = getErrorMessage(
        error,
        "Failed to update technology"
      );
      const { formData } = variables;

      // Show specific error messages for archive/unarchive operations
      if (formData.archived !== undefined) {
        const action = formData.archived ? "archive" : "unarchive";
        toast.error(`Failed to ${action} technology`, {
          description: errorMessage,
          duration: 5000,
        });
      } else {
        toast.error("Error updating technology", {
          description: errorMessage,
          duration: 5000,
        });
      }
    },
  });
};

/**
 * Hook to delete an existing technology.
 * Invalidates all related caches including account data.
 */
export const useDeleteTechnology = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { technologyId: string }>({
    mutationFn: async ({ technologyId }) => {
      await deleteTechnology(technologyId);
    },
    onSuccess: (_, { technologyId }) => {
      // Remove the technology from the detail cache
      queryClient.removeQueries({
        queryKey: technologyKeys.detail(technologyId),
      });

      // Invalidate all technology lists (this covers all account lists)
      queryClient.invalidateQueries({
        queryKey: technologyKeys.all,
      });

      // Invalidate related objects queries
      queryClient.invalidateQueries({
        queryKey: ["technologyRelatedObjects", technologyId],
      });

      // Invalidate all accounts to update last updated dates
      queryClient.invalidateQueries({
        queryKey: accountKeys.all,
      });

      toast.success("Technology deleted successfully!", {
        description: "The technology has been deleted.",
        duration: 3000,
      });
    },
    onError: (error) => {
      showErrorToast(error, {
        title: "Error deleting technology",
        defaultMessage: "Failed to delete technology",
        duration: 5000,
      });
    },
  });
};

/**
 * Hook to fetch all technology categories
 */
export const useTechnologyCategories = () => {
  return useQuery({
    queryKey: ["technology", "categories"],
    queryFn: fetchTechnologyCategories,
  });
};
